import React, { useContext, useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';

import Iconify from 'src/components/iconify';
import { AuthContext } from 'src/auth/context';
import { useEthereum } from 'src/hooks/useEthereum';
import { TARGET_CHAIN } from 'src/config/wagmiConfig';

export default function AccountMenu() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const { user, logout } = useContext(AuthContext);
  const { connected, isBusy } = useEthereum();

  const phoneNumber = user?.phoneNumber || '';
  const userLabel = phoneNumber || 'User';
  const shortNumber = phoneNumber ? phoneNumber.slice(-2) : 'U';
  const networkName = TARGET_CHAIN?.name || 'Unknown network';

  const walletConnected = Boolean(connected);
  const logoutText = walletConnected ? 'Disconnect wallet and logout' : 'Logout from account';

  let tooltipTitle = 'Account';
  if (isBusy) {
    tooltipTitle = 'Please wait...';
  } else if (phoneNumber) {
    tooltipTitle = `Account · ${phoneNumber}`;
  }

  const menuBg = isDark
    ? alpha(theme.palette.background.paper, 0.96)
    : theme.palette.background.paper;

  const softBorder = alpha(theme.palette.text.primary, isDark ? 0.14 : 0.1);
  const mutedText = alpha(theme.palette.text.primary, isDark ? 0.68 : 0.58);

  const handleClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await Promise.resolve(logout?.());
    } finally {
      handleClose();
    }
  }, [logout, handleClose]);

  const statusCardSx = {
    p: 1.15,
    borderRadius: 2.5,
    border: '1px solid',
    borderColor: softBorder,
    bgcolor: alpha(theme.palette.text.primary, isDark ? 0.045 : 0.025),
  };

  return (
    <>
      <Tooltip title={tooltipTitle} arrow>
        <Box component="span" sx={{ display: 'inline-flex' }}>
          <Button
            id="account-menu-button"
            onClick={handleClick}
            disabled={isBusy}
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="menu"
            aria-expanded={open ? 'true' : undefined}
            aria-label="Open account menu"
            sx={{
              minWidth: 0,
              px: { xs: 0.65, sm: 0.9 },
              py: 0.55,
              gap: 1,
              borderRadius: 999,
              textTransform: 'none',
              color: 'text.primary',
              bgcolor: open
                ? alpha(theme.palette.primary.main, isDark ? 0.18 : 0.08)
                : alpha(theme.palette.text.primary, isDark ? 0.07 : 0.045),
              border: '1px solid',
              borderColor: open ? alpha(theme.palette.primary.main, 0.35) : softBorder,
              boxShadow: open
                ? `0 10px 26px ${alpha(theme.palette.primary.main, isDark ? 0.2 : 0.12)}`
                : 'none',
              transition: theme.transitions.create([
                'background-color',
                'border-color',
                'box-shadow',
                'transform',
              ]),
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, isDark ? 0.2 : 0.1),
                borderColor: alpha(theme.palette.primary.main, 0.4),
                boxShadow: `0 10px 24px ${alpha(theme.palette.primary.main, isDark ? 0.18 : 0.1)}`,
                transform: 'translateY(-1px)',
              },
              '&.Mui-disabled': {
                opacity: 0.65,
              },
            }}
          >
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  fontSize: 13,
                  fontWeight: 900,
                  bgcolor: alpha(theme.palette.primary.main, isDark ? 0.22 : 0.13),
                  color: isDark ? theme.palette.primary.light : theme.palette.primary.dark,
                  border: '1px solid',
                  borderColor: alpha(theme.palette.primary.main, isDark ? 0.35 : 0.22),
                }}
              >
                {shortNumber}
              </Avatar>
            </Box>

            <Iconify
              icon={
                open
                  ? 'material-symbols:keyboard-arrow-up-rounded'
                  : 'material-symbols:keyboard-arrow-down-rounded'
              }
              width={18}
              sx={{
                display: { xs: 'none', sm: 'block' },
                color: mutedText,
                flexShrink: 0,
              }}
            />
          </Button>
        </Box>
      </Tooltip>

      <Menu
        id="account-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        keepMounted
        transitionDuration={{ enter: 160, exit: 110 }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 1.25,
              width: 310,
              maxWidth: 'calc(100vw - 24px)',
              overflow: 'visible',
              borderRadius: 3.5,
              bgcolor: menuBg,
              backdropFilter: 'blur(18px)',
              border: '1px solid',
              borderColor: softBorder,
              boxShadow: isDark
                ? `0 24px 54px ${alpha(theme.palette.common.black, 0.48)}`
                : `0 20px 44px ${alpha(theme.palette.common.black, 0.13)}`,
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 22,
                width: 13,
                height: 13,
                bgcolor: menuBg,
                borderTop: `1px solid ${softBorder}`,
                borderLeft: `1px solid ${softBorder}`,
                transform: 'translateY(-52%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
          list: {
            'aria-labelledby': 'account-menu-button',
            sx: { p: 1 },
          },
        }}
      >
        <Box
          sx={{
            p: 1.25,
            mb: 1,
            borderRadius: 3,
            border: '1px solid',
            borderColor: alpha(theme.palette.primary.main, isDark ? 0.25 : 0.16),
            bgcolor: alpha(theme.palette.primary.main, isDark ? 0.09 : 0.045),
          }}
        >
          <Stack direction="row" spacing={1.35} alignItems="center">
            <Avatar
              sx={{
                width: 46,
                height: 46,
                fontSize: 14,
                fontWeight: 900,
                bgcolor: alpha(theme.palette.primary.main, isDark ? 0.24 : 0.14),
                color: isDark ? theme.palette.primary.light : theme.palette.primary.dark,
                border: '1px solid',
                borderColor: alpha(theme.palette.primary.main, isDark ? 0.34 : 0.2),
              }}
            >
              {shortNumber}
            </Avatar>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                noWrap
                sx={{
                  fontSize: 14,
                  lineHeight: 1.2,
                  fontWeight: 850,
                  color: 'text.primary',
                }}
              >
                {userLabel}
              </Typography>

              <Typography
                noWrap
                sx={{
                  mt: 0.3,
                  fontSize: 12,
                  color: mutedText,
                }}
              >
                SafiSwap account
              </Typography>
            </Box>

            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                color: isDark ? theme.palette.primary.light : theme.palette.primary.dark,
                bgcolor: alpha(theme.palette.primary.main, isDark ? 0.16 : 0.08),
              }}
            >
              <Iconify icon="material-symbols:verified-user-rounded" width={18} />
            </Box>
          </Stack>
        </Box>

        <Stack spacing={0.8} sx={{ mb: 0.75 }}>
          {/* <Box sx={statusCardSx}>
            <Stack direction="row" spacing={1.1} alignItems="center">
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                  bgcolor: alpha(walletColor, isDark ? 0.18 : 0.1),
                  color: walletTextColor,
                }}
              >
                <Iconify icon="material-symbols:account-balance-wallet-rounded" width={18} />
              </Box>

              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: 0.45,
                    textTransform: 'uppercase',
                    color: mutedText,
                  }}
                >
                  Wallet
                </Typography>

                <Typography
                  noWrap
                  sx={{
                    mt: 0.2,
                    fontSize: 13,
                    fontWeight: 800,
                    color: 'text.primary',
                  }}
                >
                  {walletText}
                </Typography>

                <Typography
                  noWrap
                  sx={{
                    mt: 0.15,
                    fontSize: 11.5,
                    color: mutedText,
                  }}
                >
                  {walletSubText}
                </Typography>
              </Box>
            </Stack>
          </Box> */}

          <Box sx={statusCardSx}>
            <Stack direction="row" spacing={1.1} alignItems="center">
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                  bgcolor: alpha(theme.palette.info.main, isDark ? 0.18 : 0.1),
                  color: isDark ? theme.palette.info.light : theme.palette.info.dark,
                }}
              >
                <Iconify icon="material-symbols:lan-rounded" width={18} />
              </Box>

              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: 0.45,
                    textTransform: 'uppercase',
                    color: mutedText,
                  }}
                >
                  Network
                </Typography>

                <Typography
                  noWrap
                  sx={{
                    mt: 0.2,
                    fontSize: 13,
                    fontWeight: 800,
                    color: 'text.primary',
                  }}
                >
                  {networkName}
                </Typography>

                <Typography
                  noWrap
                  sx={{
                    mt: 0.15,
                    fontSize: 11.5,
                    color: mutedText,
                  }}
                >
                  Current target chain
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Stack>

        <Divider sx={{ my: 0.75, borderColor: softBorder }} />

        <MenuItem
          onClick={handleLogout}
          disabled={isBusy}
          sx={{
            mt: 0.25,
            px: 1.1,
            py: 1,
            minHeight: 54,
            borderRadius: 2.5,
            color: isDark ? theme.palette.error.light : theme.palette.error.dark,
            transition: theme.transitions.create(['background-color']),
            '&:hover': {
              bgcolor: alpha(theme.palette.error.main, isDark ? 0.13 : 0.08),
            },
            '&.Mui-focusVisible': {
              bgcolor: alpha(theme.palette.error.main, isDark ? 0.15 : 0.1),
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: 1.2,
              color: 'inherit',
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                bgcolor: alpha(theme.palette.error.main, isDark ? 0.18 : 0.1),
              }}
            >
              <Iconify icon="material-symbols:logout-rounded" width={18} />
            </Box>
          </ListItemIcon>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontSize: 13.5,
                lineHeight: 1.15,
                fontWeight: 850,
                color: 'inherit',
              }}
            >
              Logout
            </Typography>

            <Typography
              noWrap
              sx={{
                mt: 0.25,
                fontSize: 11.5,
                color: alpha(isDark ? theme.palette.error.light : theme.palette.error.dark, 0.78),
              }}
            >
              {logoutText}
            </Typography>
          </Box>
        </MenuItem>
      </Menu>
    </>
  );
}
